<template>
  <div class="personalProfilPageCardWrapper">
    <Card class="personalProfilPageCard" :footerDivider="false" :headerDivider="false">
      <template v-slot:content>
        <div class="personalProfilSections">
          <section class="personalProfilSection">
            <h2 class="personalProfilSection__title">{{ $t('personalData.title') }}</h2>
            <div class="profilBlockList">
              <ProfilBlock
                v-for="entry in personalDataEntries"
                :key="entry.route"
                :buttonLabel="entry.buttonLabel"
                :disableStyles="false"
                :iconClass="entry.iconClass"
                :subTitle="entry.subTitle"
                :title="entry.title"
                @ctaClick="goToRoute(entry.route)"
              ></ProfilBlock>
            </div>
          </section>

          <section class="personalProfilSection">
            <h2 class="personalProfilSection__title">{{ $t('contact.title') }}</h2>
            <div class="profilBlockList">
              <ProfilBlock
                v-for="entry in contactEntries"
                :key="entry.route"
                :buttonLabel="entry.buttonLabel"
                :disableStyles="false"
                :iconClass="entry.iconClass"
                :subTitle="entry.subTitle"
                :title="entry.title"
                @ctaClick="goToRoute(entry.route)"
              ></ProfilBlock>
            </div>
          </section>

          <section class="personalProfilSection">
            <h2 class="personalProfilSection__title">{{ $t('settings.title') }}</h2>
            <div class="profilBlockList">
              <ProfilBlock
                v-for="entry in settingsEntries"
                :key="entry.route"
                :buttonLabel="entry.buttonLabel"
                :disableStyles="false"
                :iconClass="entry.iconClass"
                :subTitle="entry.subTitle"
                :title="entry.title"
                @ctaClick="goToRoute(entry.route)"
              ></ProfilBlock>
            </div>
          </section>
        </div>
      </template>
    </Card>
  </div>
</template>

<script lang="ts" setup>
import { Card, ProfilBlock } from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { fullNameWithSalutation } from '@/utils/PartnerUtils'
import type { Partner, PhysicalAddress } from 'partner-api'
import { getEmail, getIdLevel, getPartnerProxyId } from '@/utils/KeycloakUtils'
import {
  fetchAccountsByPPID,
  getPartnerByPartnerProxyId
} from '@/stores/accountManagementService'

interface ProfilBlockEntry {
  title: string
  subTitle: string
  iconClass: string
  buttonLabel: string
  route: string
}

const { t } = useI18n()

const goToRoute = (route: string) => {
  navigate(route)
}

const partner = ref<Partner | null>(null)
const partnerNameWithSalutation = ref('')
const partnerAddress = ref('')
const partnerEmail = ref('')
const partnerUsername = ref('')

const personalPage = globalThis.ENV_CONFIG?.SERVICE_LINKS?.PERSONAL_PAGE
const settingsPage = globalThis.ENV_CONFIG?.SERVICE_LINKS?.SETTINGS_PAGE

const changeNameRoute =
  personalPage?.CHANGE_NAME_PAGE ?? '/persoenliches-profil/name-aendern'
const changeAddressRoute =
  personalPage?.CHANGE_ADDRESS_PAGE ?? '/persoenliches-profil/adresse-aendern'
const changeBankDataRoute =
  personalPage?.CHANGE_BANKDATA_PAGE ?? '/persoenliches-profil/bankdaten-aendern'
const changeEmailRoute = settingsPage?.CHANGE_EMAIL_PAGE ?? '/persoenliches-profil/mail'
const changePasswordRoute =
  settingsPage?.CHANGE_PASSWORD_PAGE ?? '/persoenliches-profil/passwort'
const requestActivationCodeRoute =
  settingsPage?.CHANGE_SERVICECODEREQUEST_PAGE ??
  '/persoenliches-profil/freischaltcode-anfordern'
const enterActivationCodeRoute =
  settingsPage?.CHANGE_SERVICECODEINPUT_PAGE ??
  '/persoenliches-profil/freischaltcode-eingeben'
const changeUsernameRoute =
  settingsPage?.CHANGE_USERNAME_PAGE ?? '/persoenliches-profil/benutzername'
const changeAdvertisingConsentUrl: string | null =
  personalPage?.CHANGE_ADVERTISING_CONSENT_PAGE ?? null

const setUsernameFromAccount = async (partnerProxyId: string): Promise<void> => {
  try {
    const accounts = await fetchAccountsByPPID(partnerProxyId)
    partnerUsername.value = accounts[0]?.username ?? ''
  } catch {
    partnerUsername.value = ''
  }
}

onMounted(async () => {
  const partnerProxyIdValue = getPartnerProxyId()
  if (!partnerProxyIdValue) {
    throw new Error('No partner proxy id available!')
  }

  // Für Name ändern den aktuellen Namen holen
  const partnerData = await getPartnerByPartnerProxyId(partnerProxyIdValue)
  partner.value = partnerData ?? null

  // Username analog zur Username-Change-Seite aus Account-Daten
  await setUsernameFromAccount(partnerProxyIdValue)

  partnerNameWithSalutation.value = partner.value
    ? fullNameWithSalutation(partner.value)
    : t('personalData.changeName.fallbackSubTitle')

  // Für Adresse ändern die aktuelle Adresse holen
  // PostalAddress-Basistyp hat kein Diskriminator-Feld, Felder von PhysicalAddress
  // werden defensiv über isPresent() geprüft (undefined bei PostOfficeBox).
  const partnerAddressRawData = (partnerData?.partnerAddresses?.[0]?.address ??
    null) as PhysicalAddress | null

  if (!partnerAddressRawData) return

  // Only add address parts when they are present (not undefined/null/empty)
  const isPresent = (v: unknown) =>
    v !== undefined && v !== null && String(v).trim() !== ''

  const streetParts: string[] = []
  if (isPresent(partnerAddressRawData.street))
    streetParts.push(String(partnerAddressRawData.street).trim())
  if (isPresent(partnerAddressRawData.streetExtra))
    streetParts.push(String(partnerAddressRawData.streetExtra).trim())
  const streetLine = streetParts.join(' ')

  const cityParts: string[] = []
  if (isPresent(partnerAddressRawData.postCode))
    cityParts.push(String(partnerAddressRawData.postCode).trim())
  if (isPresent(partnerAddressRawData.city))
    cityParts.push(String(partnerAddressRawData.city).trim())
  const cityLine = cityParts.join(' ')

  const fullAddressAsString = [streetLine, cityLine].filter(Boolean).join(' • ')

  partnerAddress.value = fullAddressAsString || ''

  // Für E-Mail ändern die aktuelle E-Mail aus Token holen
  partnerEmail.value = getEmail() ?? ''
})

const idLevelIsZero = computed(() => getIdLevel() === 0)

const personalDataEntries = computed<ProfilBlockEntry[]>(() => {
  return [
    {
      title: t('personalData.changeName.title'),
      subTitle: partnerNameWithSalutation.value,
      iconClass: 'icon-person',
      buttonLabel: t('continueButton'),
      route: changeNameRoute
    },
    {
      title: t('personalData.changeAddress.title'),
      subTitle: partnerAddress.value,
      iconClass: 'icon-adresse',
      buttonLabel: t('continueButton'),
      route: changeAddressRoute
    },
    {
      title: t('personalData.bankDetails.title'),
      subTitle: t('moreInfoSubTitle'),
      iconClass: 'icon-bankdaten',
      buttonLabel: t('continueButton'),
      route: changeBankDataRoute
    }
  ]
})

const contactEntries = computed<ProfilBlockEntry[]>(() => {
  return [
    {
      title: t('contact.email.title'),
      subTitle: partnerEmail.value,
      iconClass: 'icon-vertragsservice',
      buttonLabel: t('continueButton'),
      route: changeEmailRoute
    }
  ]
})

const settingsEntries = computed<ProfilBlockEntry[]>(() => {
  const entries: ProfilBlockEntry[] = [
    {
      title: t('settings.username.title'),
      subTitle: partnerUsername.value || t('settings.username.fallbackSubTitle'),
      iconClass: 'icon-person',
      buttonLabel: t('continueButton'),
      route: changeUsernameRoute
    },
    {
      title: t('settings.password.title'),
      subTitle: t('settings.password.subTitle'),
      iconClass: 'icon-passwort',
      buttonLabel: t('continueButton'),
      route: changePasswordRoute
    }
  ]
  if (changeAdvertisingConsentUrl) {
    entries.push({
      title: t('personalData.changeAdvertisingConsent.title'),
      subTitle: t('personalData.changeAdvertisingConsent.fallbackSubTitle'),
      iconClass: 'icon-uwg',
      buttonLabel: t('continueButton'),
      route: changeAdvertisingConsentUrl
    })
  }
  if (idLevelIsZero.value) {
    entries.push(
      {
        title: t('settings.requestActivationCode.title'),
        subTitle: t('settings.requestActivationCode.subTitle'),
        iconClass: 'icon-vertragsservice',
        buttonLabel: t('settings.requestActivationCode.continueButton'),
        route: requestActivationCodeRoute
      },
      {
        title: t('settings.enterActivationCode.title'),
        subTitle: t('settings.enterActivationCode.subTitle'),
        iconClass: 'icon-vertragsservice',
        buttonLabel: t('settings.requestActivationCode.continueButton'),
        route: enterActivationCodeRoute
      }
    )
  }
  return entries
})
</script>

<style lang="scss">
.personalProfilPageCardWrapper {
  --personal-profil-bg: var(--primary-color-5-4);
  --personal-profil-radius: 22px;

  .personalProfilPageCard,
  .card-outer,
  .card,
  .card--lg {
    border: none !important;
    box-shadow: none !important;
    background-color: var(--personal-profil-bg) !important;
    border-radius: var(--personal-profil-radius) !important;
    overflow: visible !important;
  }

  .personalProfilPageCard {
    .card__content,
    .card_content {
      padding: 0 !important;
      background-color: var(--personal-profil-bg) !important;
      border-radius: var(--personal-profil-radius) !important;
      overflow: visible !important;
    }
  }
}

@media (max-width: 599px) {
  .wc-container:has(> .personalProfilPageCardWrapper) {
    padding: 24px 0 !important;
  }
}

.theme-da .personalProfilPageCardWrapper {
  --personal-profil-bg: var(--invert-color, white);
}

.personalProfilSections {
  display: flex;
  flex-direction: column;
  gap: 0;
  background-color: var(--personal-profil-bg);
  padding: 32px;
  border-radius: var(--personal-profil-radius);

  @media (max-width: 599px) {
    padding: 18px;
  }
}

.personalProfilSection {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 24px;

  &:first-child {
    margin-top: 0;
  }

  @media (max-width: 599px) {
    margin-top: 24px;

    &:first-child {
      margin-top: 0;
    }
  }

  &__title {
    margin: 0 0 20px !important;
    color: var(--color-text);

    @media (max-width: 599px) {
      margin-bottom: 12px !important;
    }
  }
}

.profilBlockList {
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 599px) {
    .profil-block {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .profil-block__content,
    .profil-block__text,
    .profil-block__description,
    .profil-block__action {
      width: 100%;
      max-width: 100%;
    }

    .profil-block__action {
      margin-left: 0;
    }
  }

  .profil-block__action .button.button--small {
    width: 100%;

    @media (min-width: 600px) {
      width: 116px;
    }
  }
}

@media (max-width: 495px) {
  .is-styled .head__title h1,
  .wc-outer .head__title h1 {
    font-family: var(--h1-font-family-mobile), serif !important;
    font-size: var(--h1-font-size-mobile) !important;
    line-height: var(--h1-line-height-mobile) !important;
  }

  .personalProfilPageCardWrapper h2.personalProfilSection__title {
    font-family: var(--h2-font-family-mobile), serif !important;
    font-size: 16px !important;
    line-height: 24px !important;
  }
}
</style>
